import { useMemo } from 'react'
import type { Variants, Transition } from 'framer-motion'

// ============================================
// DESIGN TOKENS - Unified Design System
// ============================================

const designTokens = {
  // Colors - Using CSS variables from index.css
  colors: {
    primary: {
      DEFAULT: 'hsl(var(--primary))',
      foreground: 'hsl(var(--primary-foreground))',
      100: 'hsl(var(--primary-100))',
      300: 'hsl(var(--primary-300))',
      600: 'hsl(var(--primary-600))',
      700: 'hsl(var(--primary-700))',
      800: 'hsl(var(--primary-800))',
    },
    background: 'hsl(var(--background))',
    foreground: 'hsl(var(--foreground))',
    card: 'hsl(var(--card))',
    cardForeground: 'hsl(var(--card-foreground))',
    muted: 'hsl(var(--muted))',
    mutedForeground: 'hsl(var(--muted-foreground))',
    border: 'hsl(var(--border))',
    destructive: 'hsl(var(--destructive))',
    destructiveForeground: 'hsl(var(--destructive-foreground))',
  },

  // Spacing - Consistent spacing scale
  spacing: {
    xs: '0.25rem',    // 4px
    sm: '0.5rem',     // 8px
    md: '1rem',       // 16px
    lg: '1.5rem',     // 24px
    xl: '2rem',       // 32px
    '2xl': '3rem',    // 48px
    '3xl': '4rem',    // 64px
  },

  // Border Radius - Consistent rounded corners
  radius: {
    sm: 'calc(var(--radius) - 4px)',
    md: 'calc(var(--radius) - 2px)',
    lg: 'var(--radius)',
    xl: '0.75rem',
    '2xl': '1rem',
    '3xl': '1.5rem',
    '4xl': '2rem',
  },

  // Typography - Font sizes and weights
  typography: {
    sizes: {
      xs: '0.75rem',
      sm: '0.875rem',
      base: '1rem',
      lg: '1.125rem',
      xl: '1.25rem',
      '2xl': '1.5rem',
      '3xl': '1.875rem',
      '4xl': '2.25rem',
    },
    weights: {
      normal: '400',
      medium: '500',
      semibold: '600',
      bold: '700',
    },
  },

  // Shadows - Consistent shadow scale
  shadows: {
    sm: '0 1px 2px 0 rgb(0 0 0 / 0.05)',
    DEFAULT: '0 1px 3px 0 rgb(0 0 0 / 0.1), 0 1px 2px -1px rgb(0 0 0 / 0.1)',
    md: '0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1)',
    lg: '0 10px 15px -3px rgb(0 0 0 / 0.1), 0 4px 6px -4px rgb(0 0 0 / 0.1)',
    xl: '0 20px 25px -5px rgb(0 0 0 / 0.1), 0 8px 10px -6px rgb(0 0 0 / 0.1)',
    glow: '0 0 20px hsl(var(--primary) / 0.1)',
    glowLg: '0 0 40px hsl(var(--primary) / 0.15)',
  },

  // Transitions - Consistent timing
  transitions: {
    fast: '150ms cubic-bezier(0.4, 0, 0.2, 1)',
    normal: '200ms cubic-bezier(0.4, 0, 0.2, 1)',
    slow: '300ms cubic-bezier(0.4, 0, 0.2, 1)',
  },

  // Z-index scale
  zIndex: {
    dropdown: 1000,
    sticky: 1020,
    fixed: 1030,
    modalBackdrop: 1040,
    modal: 1050,
    popover: 1060,
    tooltip: 1070,
  },
} as const

// ============================================
// EASING CURVES - Natural, smooth easings
// ============================================

const easings = {
  easeOutSmooth: [0.16, 1, 0.3, 1] as const,
  easeInOutSmooth: [0.4, 0, 0.2, 1] as const,
  easeOutQuick: [0.25, 0.46, 0.45, 0.94] as const,
  easeOutBounce: [0.68, -0.55, 0.265, 1.55] as const,
  easeOutExpo: [0.19, 1, 0.22, 1] as const,
  easeSpring: [0.175, 0.885, 0.32, 1.275] as const,
} as const

// ============================================
// TRANSITION PRESETS
// ============================================

const transitions = {
  fast: {
    duration: 0.15,
    ease: easings.easeOutQuick,
  } as Transition,
  normal: {
    duration: 0.3,
    ease: easings.easeOutSmooth,
  } as Transition,
  slow: {
    duration: 0.5,
    ease: easings.easeOutSmooth,
  } as Transition,
  stagger: {
    staggerChildren: 0.05,
    delayChildren: 0.1,
  } as Transition,
  staggerSlow: {
    staggerChildren: 0.08,
    delayChildren: 0.15,
  } as Transition,
} as const

// ============================================
// ANIMATION VARIANTS
// ============================================

const animations = {
  // Page transitions
  page: {
    fadeInUp: {
      initial: false as unknown as { opacity: number; y: number },
      animate: { opacity: 1, y: 0, transition: transitions.slow },
    } as Variants,
    fadeIn: {
      initial: false as unknown as { opacity: number },
      animate: { opacity: 1, transition: transitions.normal },
    } as Variants,
    scaleFade: {
      initial: false as unknown as { opacity: number; scale: number },
      animate: { opacity: 1, scale: 1, transition: { duration: 0.25, ease: easings.easeSpring } },
    } as Variants,
    slideInLeft: {
      initial: false as unknown as { opacity: number; x: number },
      animate: { opacity: 1, x: 0, transition: transitions.normal },
    } as Variants,
    slideInRight: {
      initial: false as unknown as { opacity: number; x: number },
      animate: { opacity: 1, x: 0, transition: transitions.normal },
    } as Variants,
  },

  // Component animations
  component: {
    cardHover: {
      rest: { y: 0, scale: 1 },
      hover: { y: -4, scale: 1.01, transition: transitions.fast },
      tap: { scale: 0.98, transition: { duration: 0.1 } },
    } as Variants,
    buttonPress: {
      rest: { scale: 1 },
      hover: { scale: 1.02 },
      tap: { scale: 0.97 },
    } as Variants,
    iconRotate: {
      rest: { rotate: 0 },
      hover: { rotate: 15, transition: transitions.fast },
    } as Variants,
    listItem: {
      hidden: false as unknown as { opacity: number; x: number },
      visible: { opacity: 1, x: 0, transition: transitions.normal },
    } as Variants,
    staggerContainer: {
      hidden: false as unknown as { opacity: number },
      visible: { opacity: 1, transition: transitions.stagger },
    } as Variants,
    staggerContainerSlow: {
      hidden: false as unknown as { opacity: number },
      visible: { opacity: 1, transition: transitions.staggerSlow },
    } as Variants,
  },

  // Modal animations
  modal: {
    overlay: {
      hidden: { opacity: 0 },
      visible: { opacity: 1, transition: { duration: 0.2 } },
      exit: { opacity: 0, transition: { duration: 0.15 } },
    } as Variants,
    content: {
      hidden: { opacity: 0, scale: 0.9, y: 20 },
      visible: { opacity: 1, scale: 1, y: 0, transition: { duration: 0.25, ease: easings.easeSpring } },
      exit: { opacity: 0, scale: 0.9, y: 20, transition: { duration: 0.15 } },
    } as Variants,
  },

  // Sidebar animations
  sidebar: {
    sidebar: {
      open: { x: 0, transition: { duration: 0.25, ease: easings.easeOutExpo } },
      closed: { x: -280, transition: { duration: 0.2, ease: easings.easeInOutSmooth } },
    } as Variants,
    navLink: {
      rest: { x: 0 },
      hover: { x: 4, transition: transitions.fast },
      tap: { scale: 0.98, transition: { duration: 0.1 } },
    } as Variants,
  },

  // Stats animations
  stats: {
    statsCard: {
      hidden: false as unknown as { opacity: number; y: number; scale: number },
      visible: { opacity: 1, y: 0, scale: 1, transition: { duration: 0.35, ease: easings.easeSpring } },
    } as Variants,
    counter: {
      hidden: false as unknown as { opacity: number },
      visible: { opacity: 1, transition: { duration: 0.3 } },
    } as Variants,
    treeItem: {
      hidden: false as unknown as { opacity: number; x: number },
      visible: { opacity: 1, x: 0, transition: transitions.normal },
    } as Variants,
  },

  // Content animations
  content: {
    header: {
      hidden: false as unknown as { opacity: number; y: number },
      visible: { opacity: 1, y: 0, transition: { duration: 0.4, ease: easings.easeOutSmooth } },
    } as Variants,
    section: {
      hidden: false as unknown as { opacity: number; y: number },
      visible: { opacity: 1, y: 0, transition: transitions.normal },
    } as Variants,
    gridItem: {
      hidden: false as unknown as { opacity: number; y: number; scale: number },
      visible: { opacity: 1, y: 0, scale: 1, transition: { duration: 0.3, ease: easings.easeSpring } },
    } as Variants,
  },

  // Feedback animations
  feedback: {
    spinner: {
      animate: { rotate: 360, transition: { duration: 1, repeat: Infinity, ease: 'linear' } },
    } as Variants,
    checkmark: {
      hidden: { pathLength: 0, opacity: 0 },
      visible: { pathLength: 1, opacity: 1, transition: { pathLength: { duration: 0.4, ease: easings.easeOutSmooth }, opacity: { duration: 0.2 } } },
    } as Variants,
    shake: {
      shake: { x: [-8, 8, -8, 8, 0], transition: { duration: 0.4 } },
    } as Variants,
    pulse: {
      animate: { scale: [1, 1.05, 1], transition: { duration: 2, repeat: Infinity, ease: 'easeInOut' } },
    } as Variants,
  },
} as const

// ============================================
// STYLE PATTERNS - Reusable class combinations
// ============================================

const patterns = {
  // Card patterns
  card: {
    base: 'rounded-4xl border border-border bg-card text-card-foreground shadow-sm hover:shadow-lg hover:border-primary/30 transition-all duration-300 hover-lift backdrop-blur-sm',
    elevated: 'rounded-4xl border border-border bg-card text-card-foreground shadow-lg hover:shadow-xl hover:border-primary/30 transition-all duration-300 hover-lift backdrop-blur-sm',
    glass: 'rounded-4xl border border-border/50 bg-card/80 text-card-foreground shadow-sm backdrop-blur-md',
  },

  // Button patterns
  button: {
    primary: 'inline-flex items-center justify-center whitespace-nowrap rounded-xl text-sm font-medium font-semibold tracking-wide ring-offset-background transition-all duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/50 focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 disabled:cursor-not-allowed hover-lift active:scale-95 bg-primary text-primary-foreground hover:bg-primary/90 shadow-md hover:shadow-lg active:shadow-sm',
    secondary: 'inline-flex items-center justify-center whitespace-nowrap rounded-xl text-sm font-medium font-semibold tracking-wide ring-offset-background transition-all duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/50 focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 disabled:cursor-not-allowed hover-lift active:scale-95 bg-secondary text-secondary-foreground hover:bg-secondary/80',
    outline: 'inline-flex items-center justify-center whitespace-nowrap rounded-xl text-sm font-medium font-semibold tracking-wide ring-offset-background transition-all duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/50 focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 disabled:cursor-not-allowed hover-lift active:scale-95 border border-border bg-background hover:bg-accent hover:text-accent-foreground hover:border-primary/50',
    ghost: 'inline-flex items-center justify-center whitespace-nowrap rounded-xl text-sm font-medium font-semibold tracking-wide ring-offset-background transition-all duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/50 focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 disabled:cursor-not-allowed hover-lift active:scale-95 hover:bg-accent hover:text-accent-foreground',
    destructive: 'inline-flex items-center justify-center whitespace-nowrap rounded-xl text-sm font-medium font-semibold tracking-wide ring-offset-background transition-all duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-destructive/50 focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 disabled:cursor-not-allowed hover-lift active:scale-95 bg-destructive text-destructive-foreground hover:bg-destructive/90 shadow-md hover:shadow-lg',
    filament: 'inline-flex items-center justify-center whitespace-nowrap rounded-xl text-sm font-medium font-semibold tracking-wide ring-offset-background transition-all duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/50 focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 disabled:cursor-not-allowed hover-lift active:scale-95 bg-gradient-to-r from-primary-300 to-primary text-primary-foreground hover:from-primary-300/90 hover:to-primary/80 shadow-lg hover:shadow-xl shadow-primary/20 hover:shadow-primary/30',
  },

  // Input patterns
  input: {
    base: 'flex h-10 w-full rounded-xl border border-border bg-input px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/50 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 transition-all duration-200',
    error: 'flex h-10 w-full rounded-xl border border-destructive bg-input px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-destructive/50 focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 transition-all duration-200',
  },

  // Layout patterns
  layout: {
    page: 'min-h-screen bg-background',
    container: 'mx-auto max-w-7xl px-4 sm:px-6 lg:px-8',
    section: 'py-6 lg:py-8',
    cardGrid: 'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6',
    contentGrid: 'grid grid-cols-1 lg:grid-cols-3 gap-6',
  },

  // Text patterns
  text: {
    gradient: 'bg-gradient-to-r from-primary-300 to-primary bg-clip-text text-transparent',
    muted: 'text-muted-foreground',
    primary: 'text-primary',
    heading: 'text-foreground font-bold tracking-tight',
    subheading: 'text-lg text-muted-foreground',
  },

  // Status patterns
  status: {
    success: 'bg-green-500/20 text-green-100 border border-green-400/30',
    warning: 'bg-yellow-500/20 text-yellow-100 border border-yellow-400/30',
    error: 'bg-red-500/20 text-red-100 border border-red-400/30',
    info: 'bg-blue-500/20 text-blue-100 border border-blue-400/30',
  },

  // Icon container patterns
  icon: {
    sm: 'h-8 w-8 rounded-lg flex items-center justify-center',
    md: 'h-10 w-10 rounded-xl flex items-center justify-center',
    lg: 'h-14 w-14 rounded-xl flex items-center justify-center',
    primary: 'bg-gradient-to-br from-primary/20 to-primary/10 text-primary',
    secondary: 'bg-gradient-to-br from-primary-300/20 to-primary-300/10 text-primary-300',
  },

  // Navigation patterns
  nav: {
    link: 'group flex items-center gap-3 rounded-xl px-4 py-3 text-sm font-medium transition-all',
    linkActive: 'bg-primary/20 text-primary shadow-lg',
    linkInactive: 'bg-secondary text-secondary-foreground hover:bg-primary hover:text-primary-foreground',
  },

  // Loading patterns
  loading: {
    spinner: 'h-12 w-12 rounded-full border-4 border-muted border-t-primary animate-spin',
    skeleton: 'bg-muted rounded-lg animate-pulse',
    shimmer: 'shimmer',
  },

  // Glass effect
  glass: 'backdrop-blur-md bg-card/80 border border-border/50',
  glassDark: 'backdrop-blur-md bg-background/80 border border-border/50',

  // Utility helpers
  utils: {
    cx: (...classes: (string | undefined | null | false)[]) => {
      return classes.filter(Boolean).join(' ')
    },
  },

  // Border radius shortcuts
  radius: {
    sm: 'rounded-sm',
    md: 'rounded-md',
    lg: 'rounded-lg',
    xl: 'rounded-xl',
    '2xl': 'rounded-2xl',
    '3xl': 'rounded-3xl',
    '4xl': 'rounded-4xl',
    full: 'rounded-full',
  },
} as const

// ============================================
// UTILITY FUNCTIONS
// ============================================

const utils = {
  // Combine multiple class patterns
  cx: (...classes: (string | undefined | null | false)[]) => {
    return classes.filter(Boolean).join(' ')
  },

  // Get gradient class for index
  getGradient: (index: number, gradients?: string[]) => {
    const defaultGradients = [
      'from-primary to-primary-600',
      'from-primary-300 to-primary',
      'from-primary-600 to-primary-800',
      'from-primary-800 to-primary',
    ]
    const grads = gradients || defaultGradients
    return grads[index % grads.length]
  },

  // Get status color class
  getStatusColor: (status: 'success' | 'warning' | 'error' | 'info' | string) => {
    const statusMap: Record<string, string> = {
      success: 'bg-green-500/20 text-green-100 border border-green-400/30',
      warning: 'bg-yellow-500/20 text-yellow-100 border border-yellow-400/30',
      error: 'bg-red-500/20 text-red-100 border border-red-400/30',
      info: 'bg-blue-500/20 text-blue-100 border border-blue-400/30',
      active: 'bg-green-500/20 text-green-100 border border-green-400/30',
      pending: 'bg-yellow-500/20 text-yellow-100 border border-yellow-400/30',
      inactive: 'bg-muted text-muted-foreground border border-border',
    }
    return statusMap[status] || statusMap.info
  },

  // Format currency
  formatCurrency: (value: number | string, currency = 'USD') => {
    const num = typeof value === 'string' ? parseFloat(value) : value
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency,
    }).format(num || 0)
  },

  // Format date
  formatDate: (date: string | Date, options?: Intl.DateTimeFormatOptions) => {
    const d = typeof date === 'string' ? new Date(date) : date
    return d.toLocaleDateString('en-US', options || {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    })
  },

  // Truncate text
  truncate: (text: string, length: number) => {
    if (text.length <= length) return text
    return text.slice(0, length) + '...'
  },
} as const



// ============================================
// HOOK EXPORT
// ============================================

export function useDesignSystem() {
  return useMemo(() => ({
    tokens: designTokens,
    easings,
    transitions,
    animations,
    patterns,
    utils,
  }), [])
}

// Export individual pieces for tree-shaking
export { designTokens, easings, transitions, animations, patterns, utils }

// Default export
export default useDesignSystem
